'use client';

import React, { useState, useEffect } from 'react';
import { CheckCircle2, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface PricingFeature {
  id: string;
  text: string;
  included: boolean;
}

interface PricingPlan {
  id: string;
  plan_key: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_period: string;
  features: PricingFeature[];
  is_active: boolean;
  is_featured: boolean;
  display_order: number;
}

const PLAN_STYLES: Record<string, { color: string; iconColor: string; buttonClass: string }> = {
  starter: { color: 'border-border/40', iconColor: 'text-muted-foreground', buttonClass: 'btn-outline' },
  growth: { color: 'border-primary/50 ring-1 ring-primary/20', iconColor: 'text-primary', buttonClass: 'btn-primary' },
  enterprise: { color: 'border-secondary/40', iconColor: 'text-secondary', buttonClass: 'btn-outline' },
};

const FALLBACK_PLANS: PricingPlan[] = [
  {
    id: 'plan-starter', plan_key: 'starter', name: 'Starter',
    description: 'Perfect for small businesses getting online', price: 14999,
    currency: 'INR', billing_period: 'project', is_active: true, is_featured: false, display_order: 1,
    features: [
      { id: 'f-s-1', text: 'Up to 5 pages', included: true },
      { id: 'f-s-2', text: 'Responsive design', included: true },
      { id: 'f-s-3', text: 'Contact form', included: true },
      { id: 'f-s-4', text: 'Basic SEO setup', included: true },
      { id: 'f-s-5', text: '3 months support', included: true },
      { id: 'f-s-6', text: 'E-commerce features', included: false },
      { id: 'f-s-7', text: 'Custom animations', included: false },
      { id: 'f-s-8', text: 'CMS integration', included: false },
    ],
  },
  {
    id: 'plan-growth', plan_key: 'growth', name: 'Growth',
    description: 'For growing businesses ready to scale', price: 38999,
    currency: 'INR', billing_period: 'project', is_active: true, is_featured: true, display_order: 2,
    features: [
      { id: 'f-g-1', text: 'Up to 15 pages', included: true },
      { id: 'f-g-2', text: 'Responsive design', included: true },
      { id: 'f-g-3', text: 'Advanced forms + CRM', included: true },
      { id: 'f-g-4', text: 'Full SEO + Schema markup', included: true },
      { id: 'f-g-5', text: '6 months support', included: true },
      { id: 'f-g-6', text: 'Basic e-commerce (up to 50 products)', included: true },
      { id: 'f-g-7', text: 'Custom animations', included: true },
      { id: 'f-g-8', text: 'CMS integration', included: false },
    ],
  },
  {
    id: 'plan-enterprise', plan_key: 'enterprise', name: 'Enterprise',
    description: 'Full-scale digital solution for ambitious brands', price: 88999,
    currency: 'INR', billing_period: 'project', is_active: true, is_featured: false, display_order: 3,
    features: [
      { id: 'f-e-1', text: 'Unlimited pages', included: true },
      { id: 'f-e-2', text: 'Responsive + PWA', included: true },
      { id: 'f-e-3', text: 'Custom integrations', included: true },
      { id: 'f-e-4', text: 'Enterprise SEO + Analytics', included: true },
      { id: 'f-e-5', text: '12 months priority support', included: true },
      { id: 'f-e-6', text: 'Full e-commerce platform', included: true },
      { id: 'f-e-7', text: 'Advanced animations + 3D', included: true },
      { id: 'f-e-8', text: 'Headless CMS + API', included: true },
    ],
  },
];

export default function PricingSection() {
  const [plans, setPlans] = useState<PricingPlan[]>(FALLBACK_PLANS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('pricing_plans')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        if (!error && data && data.length > 0) {
          setPlans(data as PricingPlan[]);
        }
      } catch {
        // Use fallback
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  return (
    <section id="pricing" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-14">
          <span className="section-label">Pricing</span>
          <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
            Transparent Pricing,{' '}
            <span className="text-gradient-primary">No Surprises</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            Starting prices for common project types. Every project is scoped individually — get a free custom quote.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => (
              <div key={i} className="glass-card rounded-2xl p-7 border border-border/40 animate-pulse">
                <div className="h-6 bg-muted/50 rounded mb-4 w-1/3" />
                <div className="h-10 bg-muted/50 rounded mb-4 w-1/2" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map(j => <div key={j} className="h-4 bg-muted/30 rounded" />)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans?.map((plan) => {
              const style = PLAN_STYLES[plan?.plan_key] || PLAN_STYLES.starter;
              const features = Array.isArray(plan?.features) ? plan.features : [];
              return (
                <div
                  key={plan?.id}
                  className={`relative glass-card rounded-2xl p-7 border ${style.color} card-hover`}
                >
                  {plan?.is_featured && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-primary text-white text-[11px] font-bold font-display px-4 py-1.5 rounded-full shadow-glow-primary">
                      <Sparkles size={10} />
                      Most Popular
                    </div>
                  )}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap size={16} className={style.iconColor} />
                      <h3 className="font-display font-bold text-lg text-foreground">{plan?.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{plan?.description}</p>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-display font-extrabold stat-value text-foreground">
                        ₹{plan?.price?.toLocaleString('en-IN')}
                      </span>
                      <span className="text-sm text-muted-foreground mb-1">/{plan?.billing_period || 'project'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Starting price — final quote after discovery</p>
                  </div>
                  <div className="space-y-3 mb-7">
                    {features?.map((feature, idx) => (
                      <div key={feature?.id || idx} className="flex items-center gap-2.5">
                        <CheckCircle2
                          size={14}
                          className={feature?.included ? 'text-emerald flex-shrink-0' : 'text-border flex-shrink-0'}
                        />
                        <span className={`text-sm ${feature?.included ? 'text-foreground/80' : 'text-muted-foreground/40 line-through'}`}>
                          {feature?.text}
                        </span>
                      </div>
                    ))}
                  </div>
                  <a href="/get-website" className={`${style.buttonClass} w-full justify-center text-sm`}>
                    Get Started <ArrowRight size={14} />
                  </a>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-sm text-muted-foreground mt-8">
          Need a custom solution?{' '}
          <a href="/get-website" className="text-accent hover:text-primary transition-colors duration-200 font-semibold">
            Request a free quote →
          </a>
        </p>
      </div>
    </section>
  );
}